import {FormControl, FormField, IconButton, Option} from 'platform/components';
import {Box, Grid, GridItem, HStack, Icon, Show, Text} from 'platform/foundation';

import {useWatch} from 'react-hook-form';

import {i18n} from '@dms/i18n';
import {useTenant} from '@dms/shared';

import {PURCHASE_PRICE_BAND_COLUMNS} from '../constants/PURCHASE_PRICE_BAND_COLUMNS';
import {PurchasePriceRuleFormValues} from '../schemas/PurchasePriceRuleFormSchema';

const ROW_CONTROL_HEIGHT = 8;

interface PurchasePriceRuleBandRowProps {
  control: FormControl<PurchasePriceRuleFormValues>;
  index: number;
  /** The last band is open-ended: it has no bound and cannot be removed. */
  isLast: boolean;
  /** The only band covers every price, so it is not labelled "and above". */
  isOnly: boolean;
  isReadOnly: boolean;
  onRemove: VoidFunction;
  baseTestId: (suffix: string) => string;
}

export function PurchasePriceRuleBandRow(props: PurchasePriceRuleBandRowProps) {
  const {tenantCurrency} = useTenant();
  const marginType = useWatch({
    control: props.control,
    name: `priceBands.${props.index}.marginType`,
  });

  const marginTypeOptions: Option[] = [
    {
      value: 'FIXED_AMOUNT',
      label: i18n.t('page.settings.purchasePrice.ruleEditor.bands.fixedAmount'),
    },
    {
      value: 'PERCENTAGE',
      label: i18n.t('page.settings.purchasePrice.ruleEditor.bands.percentage'),
    },
  ];

  const lastBandLabel = props.isOnly
    ? i18n.t('page.settings.purchasePrice.ruleEditor.bands.allPrices')
    : i18n.t('page.settings.purchasePrice.ruleEditor.bands.andAbove');

  const bandTestId = (suffix: string) => props.baseTestId(`band-${props.index}-${suffix}`);

  return (
    <Grid columns={PURCHASE_PRICE_BAND_COLUMNS.total} spacing={2} align="flex-start">
      <GridItem span={PURCHASE_PRICE_BAND_COLUMNS.bound}>
        <HStack spacing={2} align="flex-start">
          {props.isLast ? (
            <HStack height={ROW_CONTROL_HEIGHT} align="center" spacing={2}>
              <Text size="small">{lastBandLabel}</Text>
              <Icon value="action/lock" size={4} color="palettes.neutral.60.100" />
            </HStack>
          ) : (
            <>
              <HStack height={ROW_CONTROL_HEIGHT} align="center" shrink={0}>
                <Text size="small" color="secondary">
                  {i18n.t('page.settings.purchasePrice.ruleEditor.bands.upTo')}
                </Text>
              </HStack>
              <Box flex={1}>
                <FormField
                  control={props.control}
                  name={`priceBands.${props.index}.upperBound`}
                  type="currency"
                  currency={tenantCurrency}
                  decimalPlaces={2}
                  data-testid={bandTestId('upperBound')}
                />
              </Box>
            </>
          )}
        </HStack>
      </GridItem>

      <GridItem span={PURCHASE_PRICE_BAND_COLUMNS.marginType}>
        <FormField
          control={props.control}
          name={`priceBands.${props.index}.marginType`}
          type="choice"
          options={marginTypeOptions}
          isNotClearable
          aria-label={i18n.t('page.settings.purchasePrice.ruleEditor.bands.marginType')}
          data-testid={bandTestId('marginType')}
        />
      </GridItem>

      <GridItem span={PURCHASE_PRICE_BAND_COLUMNS.marginValue}>
        {marginType === 'PERCENTAGE' ? (
          <FormField
            control={props.control}
            name={`priceBands.${props.index}.marginValue`}
            type="number"
            decimalPlaces={2}
            suffix="%"
            aria-label={i18n.t('page.settings.purchasePrice.ruleEditor.bands.value')}
            data-testid={bandTestId('marginValue')}
          />
        ) : (
          <FormField
            control={props.control}
            name={`priceBands.${props.index}.marginValue`}
            type="currency"
            currency={tenantCurrency}
            decimalPlaces={2}
            data-testid={bandTestId('marginValue')}
          />
        )}
      </GridItem>

      <GridItem span={PURCHASE_PRICE_BAND_COLUMNS.action}>
        <Show when={!props.isLast && !props.isReadOnly}>
          <IconButton
            icon="action/delete"
            priority="tertiary"
            severity="danger"
            aria-label={i18n.t('page.settings.purchasePrice.ruleEditor.bands.remove')}
            onClick={props.onRemove}
            data-testid={bandTestId('remove')}
          />
        </Show>
      </GridItem>
    </Grid>
  );
}
